// Dashboard system types

export interface Dashboard {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  created_by: string;
  is_default: boolean;
  layout_config: LayoutConfig;
  created_at: string;
  updated_at: string;
  // DASH-22.2: Role-based default dashboard assignment
  is_role_default?: boolean;
  default_for_role_ids?: number[];
}

export interface LayoutConfig {
  cols: number;
  rowHeight: number;
}

export interface DashboardWithWidgets extends Dashboard {
  widgets: DashboardWidget[];
}

export interface DashboardWidget {
  id: string;
  dashboard_id: string;
  widget_type_id: string;
  position: WidgetPosition;
  config: Record<string, any> | null;
  dynamic_config?: Record<string, any> | null; // Dynamic widget configuration (DASH-8.5)
  is_dynamic: boolean; // DASH-8.5: Flag for hybrid rendering (true = DynamicWidget, false = legacy)
  created_at: string;
  updated_at: string;
  widget_library?: {
    name: string;
    description: string | null;
    icon: string | null;
    is_system?: boolean; // DASH-17.2: For filtering save-as-template button
  };
}

export interface WidgetPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardPermission {
  id: string;
  dashboard_id: string;
  permission_type: 'private' | 'role' | 'user' | 'tenant';
  role_id: number | null;
  user_id: string | null;
  can_edit: boolean;
  can_view: boolean;
  created_at: string;
}

export interface WidgetTemplate {
  id: string;
  name: string;
  description: string | null;
  category: 'whats-stuck' | 'this-week' | 'trending' | 'goals';
  dynamic_config: Record<string, any> | null; // Primary config field (new templates)
  default_config?: Record<string, any> | null; // Legacy config field (backward compatibility for old templates)
  query_template: string | null;
  icon: string | null;
  min_width: number;
  min_height: number;
  is_system: boolean; // DASH-17.3: System templates cannot be edited/deleted
  tenant_id: string | null; // DASH-17.3: NULL for system templates, tenant ID for custom templates
  created_at: string;
  updated_at: string;
}

// Widget configuration schema
export interface WidgetConfig {
  // Pipeline filtering (Story DASH-3.2)
  // FIXED DASH-7.0: Changed from string[] to number[] to match database bigint type
  pipelineIds?: number[];
  pipelineTypes?: ('residential' | 'commercial' | 'onboarding')[];

  // Stage filtering - hide specific stages from widget
  // FIXED DASH-7.0: Changed from string[] to number[] to match database bigint type
  hiddenStageIds?: number[];

  // Install Count Widget - Stages that represent installation complete
  // If not set, defaults to stage named "Installation Complete"
  installCompleteStageIds?: number[];

  // Site Survey Count Widget - Disposition values that count as complete
  // If not set, defaults to "Site Survey Completed"
  siteSurveyDispositions?: string[];

  // Visualization configuration (Story DASH-6.1)
  visualizationType?: 'pie' | 'bar' | 'table' | 'number';
  groupSmallSegments?: boolean; // For pie chart - group segments < 3% into "Other"
  sortOrder?: 'asc' | 'desc'; // For bar charts and table

  // Future extensibility
  timePeriod?: 'week' | 'month' | 'quarter' | 'year';
  threshold?: number;
  limit?: number;
  [key: string]: any;
}

// Input types for creating/updating
export interface CreateDashboardInput {
  name: string;
  description?: string | null;
}

export interface UpdateDashboardInput {
  name?: string;
  description?: string | null;
  is_default?: boolean;
  layout_config?: LayoutConfig;
  // DASH-22.2: Role-based default dashboard assignment
  is_role_default?: boolean;
  default_for_role_ids?: number[];
}

export interface AddWidgetInput {
  widget_type_id: string | null; // NULL for dynamic widgets (DASH-8.5)
  position: WidgetPosition;
  config?: Record<string, any> | null;
  is_dynamic?: boolean; // DASH-8.5: Set to true for dynamic widgets
}

export interface UpdateWidgetInput {
  position?: WidgetPosition;
  config?: Record<string, any> | null;
}

export interface UpdateWidgetPositionInput {
  widgetId: string;
  position: WidgetPosition;
}

// Grid layout types for react-grid-layout
export interface LayoutItem {
  i: string;        // widget ID
  x: number;        // x position (grid units)
  y: number;        // y position (grid units)
  w: number;        // width (grid units)
  h: number;        // height (grid units)
  minW?: number;    // minimum width
  minH?: number;    // minimum height
  static?: boolean; // if true, widget can't be moved/resized
}

// Widget data types
export interface StuckProjectData {
  id: string;
  project_name: string;
  current_stage_id: number;
  stage_changed_at: string;
  stages: {
    name: string;
  } | null;
  days_stuck: number;
}

export interface InstallCountData {
  weekCount: number;
  monthCount: number;
  trend: number;
}

export interface SiteSurveyCountData {
  count: number;
  period: string;
  trend: number;
}

export interface RevenueGoalData {
  actual: number;
  goal: number;
  percentage: number;
}

export interface PipelineDistributionData {
  stage_name: string;
  count: number;
  color?: string;
}

export interface RecentMessageData {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  created_at: string;
  read: boolean; // FIXED DASH-7.0: Changed from is_read to match database column
}

export interface TaskData {
  id: string;
  title: string;
  description: string | null;
  due_date: string;
  status: string;
  priority: string | null;
  is_overdue: boolean;
}

// Widget component props
export interface WidgetComponentProps {
  config?: Record<string, any>;
}

// ==================== Dynamic Widget System (EPIC DASH-008) ====================

// Data sources available for dynamic widgets
export type DataSource = 'projects' | 'appointments' | 'tasks' | 'notifications' | 'contracts';

// Aggregation types
export type AggregationType = 'count' | 'sum' | 'avg' | 'min' | 'max' | 'list' | 'percentage';

// Visualization types
export type VisualizationType = 'number' | 'pie' | 'bar' | 'table' | 'list' | 'percentage' | 'leaderboard';

// Grouped result with user name resolution (DASH-7.1)
export interface GroupedResultWithNames {
  id: string;
  name: string;
  value: number;
}

// Date range presets
export type DateRangePreset = 'all_time' | 'today' | 'yesterday' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year' | 'last_7_days' | 'last_30_days' | 'custom';

// Date range configuration
export interface DateRangeConfig {
  type: DateRangePreset;
  dateField?: 'created_at' | 'updated_at' | 'stage_changed_at' | 'date_time' | 'due_date';
  customStart?: string; // ISO date string
  customEnd?: string;   // ISO date string
}

// ==================== Dynamic User Filter (DASH-22.4) ====================

/**
 * Dynamic filter value that resolves at query execution time
 * Used for "Current User" filter that automatically shows logged-in user's data
 *
 * Pattern: Similar to date range presets - store abstract value, resolve at query time
 */
export interface DynamicFilterValue {
  type: 'current_user';
}

/**
 * User filter value - can be either a user UUID or a dynamic filter
 */
export type UserFilterValue = string | DynamicFilterValue;

/**
 * Type guard to check if a value is a dynamic filter
 */
export function isDynamicFilter(value: UserFilterValue): value is DynamicFilterValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'current_user';
}

/**
 * Type guard to check if a value is a user UUID
 */
export function isUserUuid(value: UserFilterValue): value is string {
  return typeof value === 'string';
}

// Location filters
export interface LocationFilters {
  states?: string[];      // US state codes
  cities?: string[];      // City names
  zipCodes?: string[];    // Zip codes
}

// Numeric range filters
export interface NumericRangeFilters {
  systemSizeMin?: number;
  systemSizeMax?: number;
  systemCostMin?: number;
  systemCostMax?: number;
  daysInStageMin?: number;
  daysInStageMax?: number;
}

// Boolean flag filters
export interface BooleanFlags {
  hasActiveTasks?: boolean;
  hasOverdueTasks?: boolean;
  isDeleted?: boolean;
}

// ==================== Project Field Filters (DASH-12.1) ====================

/**
 * Data type classification for project fields
 * Used to determine appropriate operators and value input UI
 */
export type FieldDataType = 'text' | 'number' | 'date' | 'boolean';

/**
 * Filter operators available based on field data type
 * - Text: equals, contains, starts_with, ends_with
 * - Number: equals, greater_than, less_than, between
 * - Date: equals, before, after, between
 * - Boolean: is_true, is_false
 * - Checklist (DASH-12.2): exists, not_exists, is_completed, not_completed
 */
export type FilterOperator =
  // Text operators
  | 'equals'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  // Number operators
  | 'greater_than'
  | 'less_than'
  | 'between'
  // Date operators
  | 'before'
  | 'after'
  // Boolean operators
  | 'is_true'
  | 'is_false'
  // Checklist-specific operators (DASH-12.2)
  | 'exists'        // Has the checklist item (any value)
  | 'not_exists'    // Missing the checklist item
  | 'is_completed'  // For CHECKBOX items (completed = true)
  | 'not_completed'; // For CHECKBOX items (completed = false or NULL)

/**
 * Filter value types for range/between operators
 */
export interface FilterRangeValue {
  min: number;
  max: number;
}

/**
 * Filter value types for date range/between operators
 */
export interface FilterDateRangeValue {
  start: string; // ISO date string
  end: string;   // ISO date string
}

/**
 * Union type for all possible filter values
 */
export type FilterValue = string | number | boolean | FilterRangeValue | FilterDateRangeValue;

/**
 * Filter configuration for a single project field or checklist item
 * Allows filtering widgets by any standard project table field or checklist item field
 *
 * @example Standard field
 * {
 *   field: 'system_cost',
 *   operator: 'greater_than',
 *   value: 50000,
 *   dataType: 'number',
 *   source: 'project_table'
 * }
 *
 * @example Checklist item field (DASH-12.2)
 * {
 *   field: '123',
 *   operator: 'after',
 *   value: '2024-01-01',
 *   dataType: 'date',
 *   source: 'checklist_item',
 *   checklistItemId: '123',
 *   checklistItemName: 'Installation Date'
 * }
 */
export interface ProjectFieldFilter {
  /** Field name in projects table OR checklist item ID (for checklist items) */
  field: string;
  /** Comparison operator (auto-populated based on dataType) */
  operator: FilterOperator;
  /** Value to filter by (type depends on dataType and operator) */
  value: FilterValue;
  /** Data type of the field (determines available operators and value input) */
  dataType: FieldDataType;

  // DASH-12.2: Checklist item support
  /** Source of the field (project table or checklist item) */
  source?: 'project_table' | 'checklist_item';
  /** Checklist item ID from checklist_items table (if source='checklist_item') */
  checklistItemId?: string;
  /** Human-readable checklist item name for display (if source='checklist_item') */
  checklistItemName?: string;
}

// Complete filter configuration
export interface DynamicWidgetFilters {
  // Core filters (DASH-8.3)
  pipelineIds?: number[];
  stageIds?: number[];
  dateRange?: DateRangeConfig;

  // Advanced filters (DASH-8.4)
  // DASH-22.4: Updated to support dynamic "Current User" filter alongside regular UUIDs
  assignedUserIds?: UserFilterValue[];
  teamIds?: string[];
  statuses?: string[];
  dispositions?: string[];  // For appointments
  location?: LocationFilters;
  ranges?: NumericRangeFilters;
  flags?: BooleanFlags;

  // Activity threshold filter (DASH-11.1)
  // Show items with no activity for specified number of days
  // Used for "stuck" projects, inactive tasks, etc.
  lastActivityThreshold?: {
    days: number;                                               // Number of days of inactivity (minimum: 1)
    dateField: 'updated_at' | 'created_at' | 'completed_at';  // Which date field to check
  };

  // Project field filters (DASH-12.1)
  // Filter by any standard project table field (cost, size, dates, location, etc.)
  // Supports up to 10 filters per widget
  projectFields?: ProjectFieldFilter[];

  // Contract filters (CONTRACTS-1.11)
  // Filter projects by contract status
  hasPendingContracts?: boolean;  // Projects with contracts awaiting signature
}

// Aggregation configuration
export interface AggregationConfig {
  type: AggregationType;
  field?: string;     // For sum/avg/min/max (e.g., 'system_cost', 'system_size')
  limit?: number;     // For list aggregation (default: 20)
  offset?: number;    // For pagination in list aggregation (default: 0) - DASH-8.7
  percentageConfig?: PercentageAggregationConfig;  // For percentage aggregation (DASH-6.1)
}

// Visualization configuration
export interface VisualizationConfig {
  type: VisualizationType;
  groupBy?: string;          // For pie/bar charts (e.g., 'pipeline_id', 'stage_id', 'status')
  sortBy?: string;           // For bar/table (e.g., 'value', 'label', 'created_at')
  sortOrder?: 'asc' | 'desc';
  showTrend?: boolean;       // For number visualization
}

// Display configuration
export interface DisplayConfig {
  title?: string;
  subtitle?: string;         // Optional subtitle for widgets
  showFilters?: boolean;     // Display active filters on widget
  refreshInterval?: number;  // Milliseconds (default 30000)
}

// Goal configuration (optional, for revenue/target widgets)
export interface GoalConfig {
  target: number;
  period: 'week' | 'month' | 'quarter' | 'year';
}

// Complete dynamic widget configuration
export interface DynamicWidgetConfig {
  dataSource: DataSource;
  filters: DynamicWidgetFilters;
  aggregation: AggregationConfig;
  visualization: VisualizationConfig;
  display: DisplayConfig;
  goal?: GoalConfig;
}

// ==================== Headline Widget (DASH-14.1) ====================

/**
 * Headline widget configuration for text-only organizational widgets
 * These widgets display custom text without fetching data
 */
export interface HeadlineWidgetConfig {
  /** Text to display (required, 1-200 characters) */
  text: string;
  /** Font size preset (default: 'lg') */
  fontSize?: 'sm' | 'md' | 'lg' | 'xl';
  /** Text alignment (default: 'left') */
  alignment?: 'left' | 'center' | 'right';
  /** Optional custom color (hex code, default: theme foreground) */
  color?: string;
}

// Filter options for dropdown (loaded dynamically from tenant data)
export interface FilterOptions {
  pipelines?: Array<{ id: number; name: string; type?: string }>;
  stages?: Array<{ id: number; name: string; pipeline_id?: number }>;
  users?: Array<{ id: string; full_name: string; email?: string }>;
  teams?: Array<{ id: string; name: string }>;
  statuses?: string[];
  dispositions?: string[];
  states?: Array<{ value: string; label: string }>;
  cities?: string[];
}

// Query result from DynamicWidgetQueryService
export interface QueryResult {
  data: any;
  metadata: {
    count: number;
    executionTime: number;
    hasMore?: boolean;
  };
  trend?: {
    current: number;
    previous: number;
    change: number;
    changePercent: number;
    direction: 'up' | 'down' | 'neutral';
  };
}

// Percentage aggregation result (DASH-6.1)
export interface PercentageAggregationResult {
  percentage: number | null;
  numerator: number;
  denominator: number;
  error?: string;
}

// Percentage aggregation configuration (DASH-6.1, DASH-6.3)
export interface PercentageAggregationConfig {
  // Widget-based mode: Calculate from existing widgets (recommended)
  numeratorWidgetId?: string;  // Widget ID to use as numerator
  denominatorWidgetId?: string;  // Widget ID to use as denominator

  // Field-based mode: Direct filter configuration (advanced/fallback)
  numeratorCondition?: ProjectFieldFilter;  // Optional filter for numerator
  denominatorCondition?: ProjectFieldFilter;  // Optional filter for denominator

  // Display options (both modes)
  goal?: number;  // Optional goal percentage (0-100) for progress bar color
  showRawCounts?: boolean;  // Show raw numerator/denominator counts alongside percentage
}

// ==================== Global Timeframe Filter (DASH-10.1) ====================

// Global timeframe presets
export type GlobalTimeframePreset =
  | 'today'
  | 'yesterday'
  | 'wtd'      // Week to Date
  | 'mtd'      // Month to Date
  | 'ytd'      // Year to Date
  | 'all'      // All Time
  | 'last7'    // Last 7 Days
  | 'last30';  // Last 30 Days

// Custom date range for global filter
export interface CustomDateRange {
  start: Date;
  end: Date;
}

// Global timeframe option
export interface GlobalTimeframeOption {
  type: GlobalTimeframePreset | 'custom';
  customRange?: CustomDateRange;
}

// Global dashboard filters
export interface GlobalDashboardFilters {
  timeframe: GlobalTimeframeOption | null;
}

// ==================== Widget ID Mapping System (DASH-20.3) ====================

/**
 * Unmapped pipeline reference
 */
export interface UnmappedPipeline {
  id: number;
  name: string;
  reason: 'no_matching_name' | 'multiple_matches';
}

/**
 * Unmapped stage reference
 */
export interface UnmappedStage {
  id: number;
  name: string;
  pipelineName: string;
  reason: 'parent_pipeline_not_mapped' | 'stage_not_found_in_pipeline' | 'no_matching_name';
}

/**
 * Unmapped checklist item reference
 */
export interface UnmappedChecklistItem {
  id: number;
  name: string;
  type: string;
  templateName: string;
  reason: 'template_not_found' | 'item_not_found' | 'type_mismatch';
}

/**
 * Pipeline mapping result
 */
export interface PipelineMappingResult {
  mapped: Record<number, number>;  // sourceId -> targetId
  unmapped: UnmappedPipeline[];
}

/**
 * Stage mapping result
 */
export interface StageMappingResult {
  mapped: Record<number, number>;  // sourceId -> targetId
  unmapped: UnmappedStage[];
}

/**
 * Checklist item mapping result
 */
export interface ChecklistItemMappingResult {
  mapped: Record<number, number>;  // sourceId -> targetId
  unmapped: UnmappedChecklistItem[];
}

/**
 * Complete widget mapping result
 */
export interface MappingResult {
  dashboardId: string;
  widgetsProcessed: number;

  pipelines: PipelineMappingResult;
  stages: StageMappingResult;
  checklistItems: ChecklistItemMappingResult;

  users: {
    cleared: string[];  // All user UUIDs removed
  };

  teams: {
    cleared: string[];  // All team UUIDs removed
  };
}

/**
 * Compatibility issue record for widget_compatibility_issues table
 */
export interface CompatibilityIssue {
  id?: string;
  widget_id: string;
  dashboard_id: string;
  issue_type: 'pipeline' | 'stage' | 'checklist_item' | 'user' | 'team';
  field_path: string;
  unmapped_id: string;
  original_name: string | null;
  original_tenant_id: string;
  original_tenant_name: string | null;
  reason: string | null;
  created_at?: string;
  resolved_at?: string | null;
}

// ==================== Template Sharing System (DASH-22.0) ====================

/**
 * Result of mapping template config IDs from source to target tenant
 */
export interface TemplateMappingResult {
  targetTenantId: string;
  success: boolean;
  filtersOriginal?: number;
  filtersMapped?: number;
  filtersRemoved?: number;
  message: string;
}

/**
 * User-friendly summary of what was mapped/removed during template sharing
 */
export interface MappingSummary {
  description: string;
  originalCount: number;
  mappedCount: number;
  removedCount: number;
  userMessage: string;
}

// ==================== Widget Click-Through System (DASH-24.0) ====================

/**
 * Context for identifying which widget/segment was clicked
 * Passed from visualization to DashboardView to open ProjectListDrawer
 */
export interface WidgetClickContext {
  /** Widget ID from dashboard_widgets table */
  widgetId: string;
  /** Widget title for drawer header */
  widgetTitle: string;
  /** Label/value clicked (for pie/bar charts) - null for number/percentage clicks */
  clickedValue: string | number | null;
  /** Index for chart segments - null for non-chart visualizations */
  clickedIndex: number | null;
  /** Original widget filters to apply when fetching projects */
  baseFilters: DynamicWidgetFilters;
  /** The widget's full config for deriving additional filters */
  widgetConfig: DynamicWidgetConfig;
  /** Additional data from the clicked element (contains id for user groupings) */
  clickedData?: Record<string, unknown>;
}

/**
 * Derived filters based on click context
 * Combines base widget filters with segment-specific filters
 */
export interface DerivedFilters extends DynamicWidgetFilters {
  /** Additional filter derived from clicked segment (e.g., stage_id, user_id) */
  segmentFilter?: {
    field: string;
    value: string | number;
  };
  /**
   * SUB-5.14 · D90. The EXACT projects to list, in the order to list them.
   *
   * ⚠️ **WHEN THIS IS PRESENT IT IS THE WHOLE ANSWER**, and
   * `fetchProjectsForDrawer` applies no other filter. A portfolio figure counted
   * a set; the panel opens that set. Combining it with a predicate would let the
   * panel show fewer rows than the figure counted, which is the disagreement
   * D90 exists to make impossible.
   *
   * ⚠️ **PAGED BY SLICING THIS ARRAY, NOT BY `.range()`.** The order is the
   * figure's ranking (worst overrun first, longest untouched first), which no
   * column reproduces, and slicing keeps the request to one page of ids — far
   * under the measured 640-id ceiling in `projectListFilters.ts`, so no chunking
   * is needed here at all.
   */
  projectIds?: readonly string[];
  /**
   * SUB-5.17 · D94. WHICH DATE THE ROW SHOULD SHOW.
   *
   * ⚠️ **THE LIST MUST ANSWER THE QUESTION THE FIGURE ASKED.** Allan clicked
   * "Oldest untouched · 286d" on Tripper's Sandbox and the panel listed two
   * projects reading "6 days ago" and "131 days ago". Both rows were CORRECT —
   * both have sat in their stage for 285 days — but the panel renders
   * `updated_at`, so a figure about stage age was answered with edit recency and
   * looked like a filtering bug.
   *
   * ⚠️ It is the same family as every other defect this epic has found, with the
   * pieces the other way round: the rows agreed and the COLUMN did not.
   *
   * Absent means `updated_at`, which is what every widget drill-down has always
   * shown and must keep showing.
   */
  emphasis?: 'stage_age' | 'created' | 'updated';
}
